import * as d3 from 'd3';
import { UsageIntervalEnum, UsageReport, UsageReportItem, UsageReportItemType } from './usage-store';
import { formatNumber } from '../utils/formatter';

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const colorBlueSteel: string = '#b3d1ff';

export function renderBarChart(usageReport: UsageReport, container: HTMLElement, legendContainer: HTMLElement, showChart: (startDate: string, endDate: string) => void) {
    const dimensions = getChartDimensions(container);

    try {
        const data = usageReport.reportItems;
        const domain = buildDomain(data, usageReport.usageReportPeriod.interval);
        const barCategories = getBarCategories(usageReport.usageReportPeriod.interval);

        const fx = createXScale(domain, dimensions);
        const encodeWidthXAxis = createBarCategoryScale(barCategories, fx);
        const encodeHeightYAxis = createYScale(data, dimensions);
        const color = createColorScale(barCategories);

        const svg = createSvgContainer(dimensions);
        const tooltip = createTooltip();

        drawBars(svg, data, fx, encodeWidthXAxis, encodeHeightYAxis, color, tooltip, usageReport, showChart);
        drawHorizontalAxis(svg, fx, dimensions);
        drawVerticalAxis(svg, encodeHeightYAxis, dimensions);

        appendGraphToContainer(container, svg.node(), tooltip);
        
        const legend = createLegend(barCategories, color);
        appendLegendToContainer(legendContainer, legend.node());

    } catch (e) {
        console.error('An error occurred trying to build the bar chart:', e);
    }
}

function getChartDimensions(container: HTMLElement) {
    return {
        width: parseInt(container.style.width),
        height: parseInt(container.style.height),
        marginTop: 30,
        marginRight: 20,
        marginBottom: 30,
        marginLeft: 30,
    };
}

function createXScale(domain: string[], dimensions: any) {
    return d3.scaleBand()
        .domain(domain)
        .rangeRound([dimensions.marginLeft, dimensions.width - dimensions.marginRight])
        .paddingInner(0.1);
}

function createBarCategoryScale(barCategories: UsageReportItemType[], fx: any) {
    return d3.scaleBand()
        .domain(barCategories)
        .rangeRound([0, fx.bandwidth()])
        .padding(0.05);
}

function createYScale(data: UsageReportItem[], dimensions: any) {
    return d3.scaleLinear()
        .domain([0, d3.max(data, (d: UsageReportItem) => d.count)]).nice()
        .rangeRound([dimensions.height - dimensions.marginBottom, dimensions.marginTop]);
}

function createColorScale(barCategories: UsageReportItemType[]) {
    return d3.scaleOrdinal()
        .domain(barCategories)
        .range([colorBlueSteel, d3.color(colorBlueSteel).darker(1).formatHex()]);
}

function createSvgContainer(dimensions: any) {
    return d3.create("svg")
        .attr("width", dimensions.width)
        .attr("height", dimensions.height)
        .attr("viewBox", [0, 0, dimensions.width, dimensions.height])
        .attr("style", "max-width: 100%; height: auto;");
}

function createTooltip(): HTMLElement {
    const tooltip = document.createElement('div');
    tooltip.style.position = 'absolute';
    tooltip.style.display = 'none';
    tooltip.style.background = '#fff';
    tooltip.style.border = '1px solid #ccc';
    tooltip.style.padding = '5px';
    tooltip.style.borderRadius = '3px';
    tooltip.style.pointerEvents = 'none';
    return tooltip;
}

function drawBars(svg: any, data: UsageReportItem[], fx: any, encodeWidthXAxis: any, encodeHeightYAxis: any, color: any, tooltip: HTMLElement, usageReport: UsageReport, showChart: (startDate: string, endDate: string) => void) {
    svg.append("g")
        .selectAll()
        .data(d3.group(data, (d: UsageReportItem, index: number) => formatDate(d.xAxisValue, index, usageReport)))
        .join("g")
        .attr("transform", ([state]) => `translate(${fx(state)},0)`)
        .selectAll()
        .data(([, d]) => d)
        .join("rect")
        .attr("x", (d: UsageReportItem) => encodeWidthXAxis(d.type))
        .attr("y", (d: UsageReportItem) => encodeHeightYAxis(d.count))
        .attr("width", encodeWidthXAxis.bandwidth())
        .attr("height", (d: UsageReportItem) => encodeHeightYAxis(0) - encodeHeightYAxis(d.count))
        .attr("fill", (d: UsageReportItem) => color(d.type))
        .on("mouseover", function (event, d: UsageReportItem) {
            tooltip.innerHTML = `${d.type}: ${formatNumber(d.count)}`;
            tooltip.style.display = "block";
        })
        .on("mousemove", function (event) {
            tooltip.style.left = (event.pageX + 10) + "px";
            tooltip.style.top = (event.pageY + 10) + "px";
        })
        .on("mouseout", function () {
            tooltip.style.display = "none";
        })
        .on("click", function (event, d: UsageReportItem) {
            if (usageReport.usageReportPeriod.interval === UsageIntervalEnum.Month) {
                showChart(d.startDate, d.endDate);
            }
        });
}

function drawHorizontalAxis(svg: any, fx: any, dimensions: any) {
    svg.append("g")
        .attr("transform", `translate(0,${dimensions.height - dimensions.marginBottom})`)
        .call(d3.axisBottom(fx).tickSizeOuter(0))
        .call(g => g.selectAll(".domain").remove());
}

function drawVerticalAxis(svg: any, encodeHeightYAxis: any, dimensions: any) {
    svg.append("g")
        .attr("transform", `translate(${dimensions.marginLeft},0)`)
        .call(d3.axisLeft(encodeHeightYAxis).ticks(null, "s"))
        .call(g => g.selectAll(".domain").remove())
        .call(g => g.append("text")
            .attr("x", -10) 
            .attr("y", dimensions.marginTop - 20)
            .attr("fill", "currentColor")
            .attr("text-anchor", "middle") // Center the text vertically
            .text("↑ Usage"));
}

function createLegend(barCategories: UsageReportItemType[], color: any) {
    const legend = d3.create("div")
        .attr("class", "legend")
        .style("display", "flex")
        // .style("flex-direction", "column")
        .style("gap", "10px")
        .style("margin-bottom", "5px")
        .style("float", "right");

    barCategories.forEach(category => {
        const legendItem = legend.append("div")
            .style("display", "flex")
            .style("justify-content", "end")
            .style("align-items", "center")
            .style("gap", "5px");

        legendItem.append("span")
            .text(category);

        legendItem.append("div")
            .style("width", "15px")
            .style("height", "15px")
            .style("background-color", color(category));
    });

    return legend;
}

function appendGraphToContainer(container: HTMLElement, svg: SVGSVGElement, tooltip: HTMLElement) {
    container.innerHTML = '';
    container.appendChild(svg);
    container.appendChild(tooltip);
}

function appendLegendToContainer(legendContainer: HTMLElement, legend: HTMLElement) {
    legendContainer.innerHTML = '';
    legendContainer.appendChild(legend);
}

function formatDate(date: Date, index: number, usageReport: UsageReport): string {
    if (usageReport.usageReportPeriod.interval == UsageIntervalEnum.Day) {
        if (index == 0 || index == usageReport.reportItems.length - 1) {
            return `${monthNames[date.getMonth()]} ${date.getDate()}`;
        }
        return date.getDate().toString();
    }
    return `${date.getFullYear()} - ${date.getMonth() + 1}`;
}

function buildDomain(reportItems: UsageReportItem[], interval: UsageIntervalEnum): string[] {
    if (interval == UsageIntervalEnum.Month) {
        return reportItems.map(x => `${x.xAxisValue.getFullYear()} - ${x.xAxisValue.getMonth() + 1}`);
    }

    return reportItems.map((item, i) => {
        if (i == 0 || i == reportItems.length - 1) {
            return `${monthNames[item.xAxisValue.getMonth()]} ${item.xAxisValue.getDate()}`;
        }
        return item.xAxisValue.getDate().toString();
    });
}

function getBarCategories(interval: UsageIntervalEnum): UsageReportItemType[] {
    if (interval === UsageIntervalEnum.Day) {
        return [UsageReportItemType.Actual];
    }
    return [UsageReportItemType.Actual, UsageReportItemType.ActualPrevious];
}


// WEBPACK FOOTER //
// ./ClientApp/app/services/licensing-contract-bar-chart-service.ts